import type { LanguageCode } from '~/texts/languages';
import type { Texts } from '~/texts/runtime';

export type FooterLink = {
  labelKey: keyof typeof Texts;
  href: string;
  /** Opens in a new tab and shows the outbound-link icon. */
  external?: boolean;
};

export type FooterContact = {
  legalName: string;
  /** Street and town; the country line is a translated term (`Texts.FooterCountry`). */
  addressLines: string[];
  email: string;
  /** Only the Czech and Slovak partner lines have a phone number. */
  phone?: string;
  /** Left out where there is no site to point to (Slovak). */
  website?: string;
  ico: string;
  dic: string;
};

const ICO = '28668715';
const DIC = 'CZ28668715';

const CZECH_CONTACT: FooterContact = {
  legalName: 'JABLOTRON a.s.',
  addressLines: ['Pod Skalkou 4567/33,', '466 01 Jablonec nad Nisou'],
  email: 'partnerska.linka@jablotron.cz',
  phone: '800 900 096',
  website: 'www.jablotron.cz',
  ico: ICO,
  dic: DIC,
};

const SLOVAK_CONTACT: FooterContact = {
  legalName: 'JABLOTRON a. s.',
  addressLines: ['Pod Skalkou 4567/33', '46601 Jablonec nad Nisou'],
  email: 'partnerska.linka@jablotron.sk',
  phone: '+421 41 511 68 68',
  ico: ICO,
  dic: DIC,
};

const EXPORT_CONTACT: FooterContact = {
  legalName: 'JABLOTRON a. s.',
  addressLines: ['Pod Skalkou 4567/33', '46601 Jablonec nad Nisou'],
  email: 'export@jablotron.cz',
  website: 'www.jablotron.com',
  ico: ICO,
  dic: DIC,
};

export const FOOTER_CONTACT_BY_LANGUAGE: Record<LanguageCode, FooterContact> = {
  cs: CZECH_CONTACT,
  sk: SLOVAK_CONTACT,
  en: EXPORT_CONTACT,
  da: EXPORT_CONTACT,
  de: EXPORT_CONTACT,
  es: EXPORT_CONTACT,
  fr: EXPORT_CONTACT,
  it: EXPORT_CONTACT,
  nl: EXPORT_CONTACT,
  pl: EXPORT_CONTACT,
};

/** Czech is the site's fallback language, so an unknown code gets the Czech contact. */
export function getFooterContact(language: string): FooterContact {
  return FOOTER_CONTACT_BY_LANGUAGE[language as LanguageCode] ?? CZECH_CONTACT;
}

export const FOOTER_PARTNER_LINKS: FooterLink[] = [
  { labelKey: 'FooterPartnerOfficialWebsite', href: 'https://www.jablotron.com/', external: true },
  { labelKey: 'FooterPartnerMyCompanyApp', href: 'https://www.jablonet.net/', external: true },
  { labelKey: 'FooterPartnerEshop', href: 'https://b2b.jablotron.com/cz', external: true },
  { labelKey: 'FooterPartnerZone', href: 'https://zz.bezpecnostnicentrum.cz/login', external: true },
];

const GDPR_URL_CS = 'https://gdpr.jablotron.cz/';
const DATA_ACT_URL_CS = 'https://www.jablotron.com/podpora/soubory-ke-stazeni/data-act';
const DATA_ACT_URL_EN = 'https://www.jablotron.com/en/support/downloads/data-act';

/** jablotron.com prefixes every foreign version with its language code; Czech has its own GDPR site. */
function getGdprUrl(language: LanguageCode): string {
  return language === 'cs' ? GDPR_URL_CS : `https://www.jablotron.com/${language}/gdpr`;
}

/** Only Czech has a localised Data Act page, every other language points to the English one. */
function getDataActUrl(language: LanguageCode): string {
  return language === 'cs' ? DATA_ACT_URL_CS : DATA_ACT_URL_EN;
}

/** The "Useful information" links for the language the site is currently shown in (unknown code → Czech). */
export function getFooterUsefulInfoLinks(language: string): FooterLink[] {
  const code = language in FOOTER_CONTACT_BY_LANGUAGE ? (language as LanguageCode) : 'cs';

  return [
    { labelKey: 'FooterProductCompatibility', href: '/compatibility' },
    { labelKey: 'GDPR', href: getGdprUrl(code), external: true },
    { labelKey: 'FooterDataAct', href: getDataActUrl(code), external: true },
  ];
}
